import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowDownRight, FiArrowUpRight, FiBriefcase, FiSearch, FiX } from "react-icons/fi";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import { useGlobalContext } from "../../../context/context";
import "./InvestmentHub.css";
import "./InvestmentHubApi.css";
import { getWalletInvestmentMarketData, getWalletInvestmentPortfolio, placeWalletInvestmentOrder } from "../../../utils/investmentApi";
import { convertZarAmount, formatPlanCurrency, getAccountCurrencyCode, getBrowserRegionCurrencyCode, getZarRateTable } from "../../../utils/investmentPlans";
function getCountryValues(user) {
  const country = user && user.country;
  return [
    country && typeof country === "object" ? country.code : "",
    user && user.countryCode,
    user && user.country_code,
    country && typeof country === "object" ? country.name : country,
    user && user.countryName,
  ].map((value) => String(value || "").trim()).filter(Boolean);
}

function hasCountryInformation(user) {
  return getCountryValues(user).length > 0;
}

function isSouthAfricanUser(user) {
  return getCountryValues(user).some((value) => {
    const normalized = value.toLowerCase().replace(/[._-]+/g, " ").replace(/\s+/g, " ").trim();
    return ["za", "zaf", "south africa", "republic of south africa"].includes(normalized);
  });
}

function getBrowserRegionCountryCode() {
  if (typeof navigator === "undefined") return "";
  const locales = Array.from(navigator.languages || []);
  if (navigator.language) locales.push(navigator.language);
  for (const locale of locales) {
    const parts = String(locale || "").replace(/_/g, "-").split("-");
    const region = parts[1];
    if (/^[a-z]{2}$/i.test(region || "")) return region.toUpperCase();
  }
  return "";
}

function resolveSouthAfricanUser(primaryUser, fallbackUser) {
  if (hasCountryInformation(primaryUser)) return isSouthAfricanUser(primaryUser);
  if (hasCountryInformation(fallbackUser)) return isSouthAfricanUser(fallbackUser);
  return getBrowserRegionCountryCode() === "ZA";
}

const INVESTMENT_TIER_GROUPS = [
  { name: "Start up", plans: [
    { amount: "R1000", amountValue: 1000, earn: "R15,000", earnValue: 15000 },
    { amount: "R2000", amountValue: 2000, earn: "R25,000", earnValue: 25000 },
    { amount: "R3000", amountValue: 3000, earn: "R65,000", earnValue: 65000 },
    { amount: "R4000", amountValue: 4000, earn: "R85,000", earnValue: 85000 },
    { amount: "R5,000", amountValue: 5000, earn: "R100,000", earnValue: 100000 },
  ] },
  { name: "Pro plans", plans: [
    { amount: "R10,000", amountValue: 10000, earn: "R150,000", earnValue: 150000 },
    { amount: "R20,000", amountValue: 20000, earn: "R250,000", earnValue: 250000 },
    { amount: "R30,000", amountValue: 30000, earn: "R350,000", earnValue: 350000 },
    { amount: "R40,000", amountValue: 40000, earn: "R500,000", earnValue: 500000 },
    { amount: "R50,000", amountValue: 50000, earn: "R800,000", earnValue: 800000 },
  ] },
  { name: "VIP plans", plans: [
    { amount: "R100,000", amountValue: 100000, earn: "R1,500,000", earnValue: 1500000 },
    { amount: "R200,000", amountValue: 200000, earn: "R2,500,000", earnValue: 2500000 },
    { amount: "R300,000", amountValue: 300000, earn: "R3,500,000", earnValue: 3500000 },
    { amount: "R400,000", amountValue: 400000, earn: "R5,000,000", earnValue: 5000000 },
    { amount: "R500,000", amountValue: 500000, earn: "R10,000,000", earnValue: 10000000 },
  ] },
];

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const sharesFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 8 });

function formatWalletMoney(value, currencyCode = "USD") {
  const code = /^[A-Z]{3}$/.test(String(currencyCode || "")) ? currencyCode : "USD";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 2 }).format(Number(value) || 0);
  } catch (error) {
    return `${code} ${(Number(value) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
}

function createClientOrderId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function getAsset(assets, symbol, assetId) {
  return (assetId && assets.find((asset) => asset.id === assetId)) || assets.find((asset) => asset.symbol === symbol);
}

function getHolding(holdings, assetId, symbol) {
  return (assetId && holdings.find((holding) => (holding.assetId || holding.symbol) === assetId)) || holdings.find((holding) => holding.symbol === symbol);
}

function valueOf(holdings, assets) {
  return holdings.reduce((sum, holding) => {
    const asset = getAsset(assets, holding.symbol, holding.assetId);
    return sum + (asset ? holding.shares * asset.price : 0);
  }, 0);
}

function formatDate(timestamp) {
  return new Date(timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatCompactWalletMoney(value, currencyCode = "USD") {
  const code = /^[A-Z]{3}$/.test(String(currencyCode || "")) ? currencyCode : "USD";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(Number(value) || 0);
  } catch (error) {
    return code + " " + (Number(value) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
}

function PortfolioChart({ history, currencyCode = "USD" }) {
  const entries = useMemo(
    () => (Array.isArray(history) ? history : []).slice(-24)
      .map((entry) => ({ at: entry.at, value: Number(entry.walletBalance) }))
      .filter((entry) => Number.isFinite(entry.value)),
    [history]
  );
  const width = 700;
  const height = 240;
  const padLeft = 88;
  const padRight = 16;
  const padTop = 18;
  const padBottom = 28;
  const values = entries.map((entry) => entry.value);
  const dataMin = values.length ? Math.min(...values) : 0;
  const dataMax = values.length ? Math.max(...values) : 1;
  const dataRange = Math.max(dataMax - dataMin, Math.abs(dataMax) * 0.08, 1);
  const padding = entries.length === 1 ? dataRange * 0.5 : dataRange * 0.12;
  const minValue = dataMin - padding;
  const maxValue = dataMax + padding;
  const plotHeight = height - padTop - padBottom;
  const plotWidth = width - padLeft - padRight;
  const coordinates = entries.map((entry, index) => ({
    ...entry,
    x: entries.length < 2 ? padLeft + plotWidth / 2 : padLeft + (index / (entries.length - 1)) * plotWidth,
    y: padTop + ((maxValue - entry.value) / (maxValue - minValue)) * plotHeight,
  }));
  const linePoints = coordinates.map((point) => point.x + "," + point.y).join(" ");
  const areaPoints = coordinates.length > 1
    ? coordinates[0].x + "," + (height - padBottom) + " " + linePoints + " " + coordinates[coordinates.length - 1].x + "," + (height - padBottom)
    : "";
  const ticks = [0, 1, 2, 3].map((index) => maxValue - ((maxValue - minValue) * index) / 3);

  return (
    <div className="inv-chart-wrap">
      <svg className="inv-chart" viewBox={"0 0 " + width + " " + height} role="img" aria-label={"Available wallet balance chart with " + entries.length + " recorded balance " + (entries.length === 1 ? "snapshot" : "snapshots")}>
        <title>Available wallet balance history</title>
        <defs>
          <linearGradient id="inv-balance-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#5756d1" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#5756d1" stopOpacity="0.015" />
          </linearGradient>
        </defs>
        {ticks.map((tick, index) => {
          const y = padTop + (index / 3) * plotHeight;
          return (
            <g key={index}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} className="inv-chart-grid" />
              <text x={padLeft - 10} y={y + 3} textAnchor="end" className="inv-chart-axis-label">{formatCompactWalletMoney(tick, currencyCode)}</text>
            </g>
          );
        })}
        {coordinates.length > 1 && <polygon points={areaPoints} className="inv-chart-area" />}
        {coordinates.length > 1 && <polyline points={linePoints} className="inv-chart-line" />}
        {coordinates.length === 1 && <line x1={padLeft} x2={width - padRight} y1={coordinates[0].y} y2={coordinates[0].y} className="inv-chart-single-guide" />}
        {coordinates.map((point, index) => (
          <circle key={point.at || index} cx={point.x} cy={point.y} r={index === coordinates.length - 1 ? 5 : 3.5} className={index === coordinates.length - 1 ? "inv-chart-dot is-latest" : "inv-chart-dot"}>
            <title>{formatDate(point.at)} Â· {formatWalletMoney(point.value, currencyCode)}</title>
          </circle>
        ))}
        {coordinates.length === 1 && <text x={coordinates[0].x} y={coordinates[0].y - 13} textAnchor="middle" className="inv-chart-point-label">{formatCompactWalletMoney(coordinates[0].value, currencyCode)}</text>}
      </svg>
      {entries.length === 0 && <div className="inv-chart-empty"><span>No balance history yet</span><small>Your balance is recorded after each investment order.</small></div>}
      {entries.length === 1 && <div className="inv-chart-single-note">One snapshot recorded Â· another order adds a trend line</div>}
      {entries.length > 0 && <div className="inv-chart-labels"><span>{formatDate(entries[0].at)}</span>{entries.length > 1 && <span>{formatDate(entries[entries.length - 1].at)}</span>}</div>}
    </div>
  );
}
export default function InvestmentHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const { baseUrl, userDetails } = useGlobalContext();
  const isMarket = location.pathname === "/markets";
  const [portfolio, setPortfolio] = useState({ cash: 0, holdings: [], history: [], activity: [], currencyCode: "USD", currencySymbol: "" });
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState("");
  const [assets, setAssets] = useState([]);
  const [marketLoading, setMarketLoading] = useState(true);
  const [marketError, setMarketError] = useState("");
  const [marketWarnings, setMarketWarnings] = useState([]);
  const [marketUpdatedAt, setMarketUpdatedAt] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState(null);
  const orderAttempt = useRef(null);
  const [quantity, setQuantity] = useState("1");
  const [buyAmount, setBuyAmount] = useState("");
  const [orderError, setOrderError] = useState("");
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [userName, setUserName] = useState("Investor");
  const [planCurrency, setPlanCurrency] = useState(() => {
    try {
      const accountCode = getAccountCurrencyCode(JSON.parse(sessionStorage.getItem("user") || "{}"));
      const browserCode = accountCode ? "" : getBrowserRegionCurrencyCode();
      return { code: accountCode || browserCode, fromBrowser: !accountCode && Boolean(browserCode) };
    } catch (error) {
      const browserCode = getBrowserRegionCurrencyCode();
      return { code: browserCode, fromBrowser: Boolean(browserCode) };
    }
  });
  const [showRandAmounts, setShowRandAmounts] = useState(() => {
    try {
      return resolveSouthAfricanUser(JSON.parse(sessionStorage.getItem("user") || "{}"), {});
    } catch (error) {
      return getBrowserRegionCountryCode() === "ZA";
    }
  });
  const planCurrencyCode = planCurrency.code;
  const planCurrencyFromBrowser = planCurrency.fromBrowser;
  const localPlanCurrencyCode = !showRandAmounts && planCurrencyCode !== "ZAR" ? planCurrencyCode : "";
  const [planFx, setPlanFx] = useState({ rates: null, updatedAt: "", loading: false, error: "" });
  useEffect(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      setUserName(user.firstName || user.name || "Investor");
    } catch (error) {
      setUserName("Investor");
    }
  }, []);

  useEffect(() => {
    let storedUser = {};
    try { storedUser = JSON.parse(sessionStorage.getItem("user") || "{}"); } catch (error) { storedUser = {}; }
    const profileUser = userDetails && !Array.isArray(userDetails) ? userDetails : {};
    const accountCode = getAccountCurrencyCode(profileUser) || getAccountCurrencyCode(storedUser);
    const browserCode = accountCode ? "" : getBrowserRegionCurrencyCode();
    setPlanCurrency({ code: accountCode || browserCode, fromBrowser: !accountCode && Boolean(browserCode) });
    setShowRandAmounts(resolveSouthAfricanUser(profileUser, storedUser));
  }, [userDetails]);

  useEffect(() => {
    if (isMarket || !planCurrencyCode || planCurrencyCode === "ZAR") return undefined;
    let cancelled = false;
    setPlanFx({ rates: null, updatedAt: "", loading: true, error: "" });
    getZarRateTable()
      .then((data) => { if (!cancelled) setPlanFx({ rates: data.rates, updatedAt: data.time_last_update_utc || "", loading: false, error: "" }); })
      .catch((error) => { if (!cancelled) setPlanFx({ rates: null, updatedAt: "", loading: false, error: error.message || "Local currency rates are temporarily unavailable." }); });
    return () => { cancelled = true; };
  }, [isMarket, planCurrencyCode]);

  const refreshPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    setPortfolioError("");
    try {
      const response = await getWalletInvestmentPortfolio(baseUrl);
      const savedPortfolio = response.portfolio;
      const availableBalance = Number(savedPortfolio && (savedPortfolio.availableBalance ?? savedPortfolio.cash));
      if (!savedPortfolio || !Number.isFinite(availableBalance) || !Array.isArray(savedPortfolio.holdings) || !Array.isArray(savedPortfolio.history) || !Array.isArray(savedPortfolio.activity)) {
        throw new Error("The investment service returned an invalid wallet account.");
      }
      setPortfolio({
        cash: availableBalance,
        holdings: savedPortfolio.holdings,
        history: savedPortfolio.history,
        activity: savedPortfolio.activity,
        currencyCode: savedPortfolio.currencyCode || "USD",
        currencySymbol: savedPortfolio.currencySymbol || "",
      });
    } catch (error) {
      setPortfolioError(error.message || "Your wallet investment account could not be loaded.");
    } finally {
      setPortfolioLoading(false);
    }
  }, [baseUrl]);
  useEffect(() => {
    refreshPortfolio();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshPortfolio();
    };
    window.addEventListener("focus", refreshWhenVisible);
    return () => window.removeEventListener("focus", refreshWhenVisible);
  }, [refreshPortfolio]);

  useEffect(() => {
    let cancelled = false;
    const refreshMarketData = async () => {
      try {
        const marketData = await getWalletInvestmentMarketData(baseUrl, { category: "all" });
        if (!marketData || !Array.isArray(marketData.assets)) throw new Error("The investment service returned invalid market data.");
        if (!cancelled) {
          setAssets(marketData.assets);
          setMarketWarnings(Array.isArray(marketData.warnings) ? marketData.warnings : []);
          setMarketError("");
          setMarketUpdatedAt(marketData.updatedAt || new Date().toISOString());
        }
      } catch (error) {
        if (!cancelled) setMarketError(error.message || "Live market data is temporarily unavailable.");
      } finally {
        if (!cancelled) setMarketLoading(false);
      }
    };

    refreshMarketData();
    const refreshTimer = window.setInterval(refreshMarketData, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
    };
  }, [baseUrl]);
  const holdings = portfolio.holdings.filter((holding) => holding.shares > 0);
  const holdingsValue = valueOf(holdings, assets);
  const costBasis = holdings.reduce((sum, holding) => sum + holding.shares * (Number(holding.averageCostUsd) || 0), 0);
  const profitLoss = holdingsValue - costBasis;
  const filteredAssets = assets.filter((asset) => {
    const matchesCategory = categoryFilter === "all" || asset.type === categoryFilter;
    const searchableText = [asset.symbol, asset.name, asset.sector, asset.type].filter(Boolean).join(" " ).toLowerCase();
    return matchesCategory && searchableText.includes(search.trim().toLowerCase());
  });
  const selectedAsset = order ? getAsset(assets, order.symbol, order.assetId) : null;
  const selectedHolding = order ? getHolding(holdings, order.assetId, order.symbol) : null;
  const orderValue = selectedAsset ? Number(quantity || 0) * selectedAsset.price : 0;

  const openOrder = (asset, side) => {
    orderAttempt.current = null;
    setOrder({ symbol: asset.symbol, assetId: asset.id || asset.symbol, side });
    setQuantity("1");
    setBuyAmount("");
    setOrderError("");
  };

  const submitOrder = async (event) => {
    event.preventDefault();
    if (!selectedAsset || !order || portfolioLoading || portfolioError || orderSubmitting) return;
    const isBuy = order.side === "buy";
    let shares;
    let amount;
    if (isBuy) {
      const enteredAmount = Number(buyAmount);
      amount = Math.round((enteredAmount + Number.EPSILON) * 100) / 100;
      if (!Number.isFinite(enteredAmount) || enteredAmount <= 0 || Math.abs(enteredAmount - amount) > 0.00000001) {
        setOrderError("Enter an investment amount with up to two decimal places.");
        return;
      }
      if (amount > portfolio.cash + 0.001) {
        setOrderError("The amount is greater than your available wallet balance.");
        return;
      }
    } else {
      const enteredShares = Number(quantity);
      shares = Math.round((enteredShares + Number.EPSILON) * 100000000) / 100000000;
      if (!Number.isFinite(enteredShares) || enteredShares <= 0 || Math.abs(enteredShares - shares) > 0.000000001) {
        setOrderError("Enter a share quantity greater than zero with up to eight decimal places.");
        return;
      }
    }

    const assetId = selectedAsset.id || selectedAsset.symbol;
    const fingerprint = JSON.stringify([order.side, assetId, isBuy ? "amount" : "shares", isBuy ? amount : shares, isBuy ? portfolio.currencyCode : ""]);
    if (!orderAttempt.current || orderAttempt.current.fingerprint !== fingerprint) {
      orderAttempt.current = { fingerprint, id: createClientOrderId() };
    }
    setOrderSubmitting(true);
    setOrderError("");
    try {
      const response = await placeWalletInvestmentOrder(baseUrl, {
        side: order.side,
        symbol: selectedAsset.symbol,
        assetId,
        ...(isBuy ? { amount } : { shares }),
        clientOrderId: orderAttempt.current.id,
      });
      if (!response.portfolio) throw new Error("The investment service returned an invalid order response.");
      const updated = response.portfolio;
      const balance = Number(updated.availableBalance ?? updated.cash);
      if (!Number.isFinite(balance) || !Array.isArray(updated.holdings) || !Array.isArray(updated.history) || !Array.isArray(updated.activity)) {
        throw new Error("The investment service returned an invalid wallet account.");
      }
      setPortfolio({ cash: balance, holdings: updated.holdings, history: updated.history, activity: updated.activity, currencyCode: updated.currencyCode || portfolio.currencyCode, currencySymbol: updated.currencySymbol || portfolio.currencySymbol });
      orderAttempt.current = null;
      setOrder(null);
    } catch (error) {
      setOrderError(error.message || "Your wallet order could not be saved.");
      if (error.status === 409) refreshPortfolio();
    } finally {
      setOrderSubmitting(false);
    }
  };

  return (
    <section className="inv-shell">
      <div className="inv-topbar"><DashboardHeader pathname={userName} /></div>
      <main className="inv-content">
        <div className="inv-heading-row">
          <div>
            <span className="inv-eyebrow">PROFIT MONITORING / INVEST</span>
            <h1>{isMarket ? "Explore assets" : "Portfolio overview"}</h1>
            <p>{isMarket ? "Browse assets and invest from your available wallet balance." : "Track your available wallet balance, investments, and market value."}</p>
          </div>
          <nav className="inv-tabs" aria-label="Investment pages">
            <button type="button" className={!isMarket ? "active" : ""} onClick={() => navigate("/investments")}>Portfolio</button>
            <button type="button" className={isMarket ? "active" : ""} onClick={() => navigate("/markets")}>Asset market</button>
          </nav>
        </div>

        

        {!isMarket && (
          <section className="inv-plan-section" aria-labelledby="inv-plan-title">
            <div className="inv-plan-heading">
              <div>
             
                <h2 id="inv-plan-title">Investment plans</h2>
                <p>Return amounts supplied; they are verified or guaranteed by this application.</p>
              </div>
              <div className="inv-plan-currency"><span>Stated plan term</span><strong>48 hours</strong><span>{planCurrencyFromBrowser ? "Detected currency" : "Account currency"}</span><strong>{planCurrencyCode || "Choose in Profile"}</strong></div>
            </div>
            <p className="inv-plan-status" role="note">Automated trading insights powered by AI, with profit reports in 48 hours. Fund your wallet and let us know the plan you want to start with, Thank you</p>
            {planCurrencyFromBrowser && <p className="inv-plan-status" role="status">Using {planCurrencyCode} based on your browser region. Choose a country in Profile to save your account location.</p>}
            {!planCurrencyCode && <p className="inv-plan-status" role="status">Your account has no country set. Choose one in Profile to see local estimates.</p>}
            {planCurrencyCode && planCurrencyCode !== "ZAR" && planFx.loading && <p className="inv-plan-status" role="status">Loading the latest available daily exchange rateâ¦</p>}
            {planFx.error && <p className="inv-plan-status" role="status">{planFx.error}</p>}
            {planCurrencyCode && planCurrencyCode !== "ZAR" && !planFx.loading && !planFx.error && planFx.rates && !planFx.rates[planCurrencyCode] && <p className="inv-plan-status" role="status">A local rate is unavailable for {planCurrencyCode}; local estimates cannot be displayed right now.</p>}
            <div className="inv-plan-groups">
              {INVESTMENT_TIER_GROUPS.map((group) => (
                <section className="inv-plan-group" key={group.name}>
                  <div className="inv-plan-group-heading"><h3>{group.name}</h3><span>{group.plans.length} tiers</span></div>
                  <div className="inv-plan-cards">
                    {group.plans.map((plan) => {
                      const localInvestment = localPlanCurrencyCode ? convertZarAmount(plan.amountValue, planFx.rates, localPlanCurrencyCode) : null;
                      const localEarn = plan.earnValue !== null && localPlanCurrencyCode ? convertZarAmount(plan.earnValue, planFx.rates, localPlanCurrencyCode) : null;
                      return (
                        <article className="inv-plan-card" key={plan.amount}>
                          {showRandAmounts ? (
                            <>
                              <span className="inv-plan-card-label">Investment amount (ZAR)</span>
                              <strong className="inv-plan-zar">{plan.amount}</strong>
                              <div className="inv-plan-local"><span>Earn (stated target, ZAR)</span><strong>{plan.earn}</strong></div>
                            </>
                          ) : (
                            <>
                              <div className="inv-plan-local"><span>{localPlanCurrencyCode ? `Approx. ${localPlanCurrencyCode} investment` : "Local-currency investment"}</span><strong>{localInvestment === null ? localPlanCurrencyCode ? "Rate unavailable" : "Choose country in Profile" : formatPlanCurrency(localInvestment, localPlanCurrencyCode)}</strong></div>
                              <div className="inv-plan-local"><span>{localPlanCurrencyCode ? `Approx. ${localPlanCurrencyCode} stated target` : "Local-currency stated target"}</span><strong>{plan.earnValue === null ? "Estimate unavailable for amount as entered" : localEarn === null ? localPlanCurrencyCode ? "Rate unavailable" : "Choose country in Profile" : formatPlanCurrency(localEarn, localPlanCurrencyCode)}</strong></div>
                            </>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </section>
        )}
        {portfolioLoading && <div className="inv-market-message" role="status">Loading your wallet investment accountâ¦</div>}
        {portfolioError && <div className="inv-market-message inv-market-error" role="alert">{portfolioError}<button type="button" className="inv-link-button" onClick={refreshPortfolio}>Retry</button></div>}

        {isMarket ? (
          <>
            <div className="inv-market-summary">
              <div><span>AVAILABLE WALLET</span><strong>{formatWalletMoney(portfolio.cash, portfolio.currencyCode)}</strong></div>
              <div><span>OPEN POSITIONS</span><strong>{holdings.length}</strong></div>
              <div><span>ORDER TYPE</span><strong>Wallet-backed</strong></div>
            </div>
            <section className="inv-market-panel">
              <div className="inv-panel-heading">
                <div><span className="inv-eyebrow">MARKETS</span><h2>Available assets</h2></div>
                <div className="inv-market-controls">
                  <label className="inv-search"><FiSearch size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, ticker, or type" aria-label="Search assets" /></label>
                  <label className="inv-filter">
                    <span>Filter</span>
                    <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} aria-label="Filter assets by category">
                      <option value="all">All assets</option>
                      <option value="crypto">Cryptocurrencies</option>
                      <option value="forex">Forex</option>
                      <option value="stock">Stocks</option>
                      <option value="commodities">Commodities</option>
                    </select>
                  </label>
                </div>
              </div>
              {marketError && <div className="inv-market-message inv-market-error" role="status">{marketError}</div>}
              {marketWarnings.length > 0 && <p className="inv-market-feed-status" role="status">{marketWarnings.join(" Â· ")}</p>}
              <div className="inv-market-updated">Market data is checked every five minutes; commodity history can be cached for up to six hours{marketUpdatedAt ? " Â· Checked " + new Date(marketUpdatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}</div>
              <div className="inv-assets-table-wrap">
                <table className="inv-assets-table">
                  <thead><tr><th>Asset</th><th>Market price</th><th>Day move</th><th>Your shares</th><th>Trade</th></tr></thead>
                  <tbody>
                    {filteredAssets.map((asset) => {
                      const position = getHolding(holdings, asset.id || asset.symbol, asset.symbol);
                      return <tr key={asset.id || asset.symbol}>
                        <td><div className="inv-asset-name"><span className="inv-asset-mark" style={{ "--asset-color": asset.color }}>{asset.symbol.slice(0, 1)}</span><span><strong>{asset.name}</strong><small>{asset.symbol} Â· {asset.sector}</small></span></div></td>
                        <td className="inv-price">{money.format(asset.price)}<small>USD / share</small></td>
                        <td><span className={asset.change >= 0 ? "inv-move positive" : "inv-move negative"}>{asset.change >= 0 ? "+" : ""}{asset.change.toFixed(2)}%</span></td>
                        <td>{position ? sharesFormat.format(position.shares) : "â"}</td>
                        <td><div className="inv-trade-actions"><button type="button" className="inv-buy-btn" disabled={portfolioLoading || Boolean(portfolioError)} onClick={() => openOrder(asset, "buy")}>Buy</button><button type="button" className="inv-sell-btn" disabled={!position || portfolioLoading || Boolean(portfolioError)} onClick={() => openOrder(asset, "sell")}>Sell</button></div></td>
                      </tr>;
                    })}
                  </tbody>
                </table>
                {filteredAssets.length === 0 && <div className="inv-empty-search">{marketLoading ? "Loading market quotesâ¦" : marketError ? "Market quotes could not be loaded." : "No assets match the current search and filter."}</div>}
              </div>
              <p className="inv-table-footnote">Market prices are shown in USD. The API validates the current quote and exchange rate; orders update your Profit Monitoring wallet and are not sent to a broker.</p>
            </section>
          </>
        ) : (
          <>
            <section className="inv-metrics" aria-label="Portfolio summary">
              <article className="inv-metric-card inv-metric-primary"><span>AVAILABLE WALLET BALANCE</span><strong>{formatWalletMoney(portfolio.cash, portfolio.currencyCode)}</strong><small>Current spendable balance</small></article>
              <article className="inv-metric-card"><span>INVESTMENT VALUE (USD)</span><strong>{money.format(holdingsValue)}</strong><small>Based on current market quotes</small></article>
              <article className="inv-metric-card"><span>UNREALIZED P/L (USD)</span><strong className={profitLoss >= 0 ? "positive" : "negative"}>{profitLoss >= 0 ? "+" : "-"}{money.format(Math.abs(profitLoss))}</strong><small>Against average purchase price</small></article>
              <article className="inv-metric-card"><span>POSITIONS</span><strong>{holdings.length}</strong><small>{holdings.length === 1 ? "asset held" : "assets held"}</small></article>
            </section>

            <section className="inv-panel inv-chart-panel">
              <div className="inv-panel-heading"><div><span className="inv-eyebrow">WALLET HISTORY</span><h2>Available balance over time</h2></div><span className="inv-chart-current">Available now <b>{formatWalletMoney(portfolio.cash, portfolio.currencyCode)}</b></span></div>
              <PortfolioChart history={portfolio.history} currencyCode={portfolio.currencyCode} />
              <p className="inv-table-footnote">Available balance recorded after each order; investment holdings are tracked separately.</p>
            </section>

            <section className="inv-panel inv-holdings-panel">
              <div className="inv-panel-heading"><div><span className="inv-eyebrow">YOUR POSITIONS</span><h2>Holdings</h2></div><button type="button" className="inv-link-button" onClick={() => navigate("/markets")}>Browse assets <FiArrowUpRight size={15} /></button></div>
              {holdings.length ? <div className="inv-holdings-table-wrap"><table className="inv-assets-table inv-holdings-table"><thead><tr><th>Asset</th><th>Shares</th><th>Average cost</th><th>Market price</th><th>Market value</th><th>Unrealized P/L</th><th></th></tr></thead><tbody>{holdings.map((holding) => {
                const asset = getAsset(assets, holding.symbol, holding.assetId);
                if (!asset) return null;
                const value = holding.shares * asset.price;
                const gain = value - holding.shares * (Number(holding.averageCostUsd) || 0);
                return <tr key={holding.assetId || holding.symbol}><td><div className="inv-asset-name"><span className="inv-asset-mark" style={{ "--asset-color": asset.color }}>{asset.symbol.slice(0, 1)}</span><span><strong>{asset.name}</strong><small>{asset.symbol}</small></span></div></td><td>{sharesFormat.format(holding.shares)}</td><td>{money.format(holding.averageCostUsd)}</td><td>{money.format(asset.price)}</td><td className="inv-price">{money.format(value)}</td><td className={gain >= 0 ? "positive" : "negative"}>{gain >= 0 ? "+" : "â"}{money.format(Math.abs(gain))}</td><td><button type="button" className="inv-sell-btn" disabled={portfolioLoading || Boolean(portfolioError)} onClick={() => openOrder(asset, "sell")}>Sell</button></td></tr>;
              })}</tbody></table></div> : <div className="inv-empty-state"><span className="inv-empty-icon"><FiBriefcase size={22} /></span><strong>No holdings yet</strong><p>Your investments will appear here after your first wallet order.</p><button type="button" className="inv-buy-btn" onClick={() => navigate("/markets")}>Explore assets</button></div>}
            </section>

            <section className="inv-panel inv-activity-panel">
              <div className="inv-panel-heading"><div><span className="inv-eyebrow">ORDER HISTORY</span><h2>Recent activity</h2></div><span className="inv-activity-count">{portfolio.activity.length} orders</span></div>
              {portfolio.activity.length ? <div className="inv-activity-list">{portfolio.activity.slice(0, 5).map((item) => <div className="inv-activity-row" key={item.id}><span className={item.side === "buy" ? "inv-activity-icon buy" : "inv-activity-icon sell"}>{item.side === "buy" ? <FiArrowDownRight size={16} /> : <FiArrowUpRight size={16} />}</span><span className="inv-activity-title"><strong>{item.side === "buy" ? "Bought" : "Sold"} {item.symbol}</strong><small>{sharesFormat.format(item.shares)} shares Â· {formatDate(item.at)}</small></span><span className="inv-activity-amount">{formatWalletMoney(item.walletAmount, item.currencyCode || portfolio.currencyCode)}</span></div>)}</div> : <p className="inv-no-activity">Your wallet orders will appear here.</p>}
            </section>
          </>
        )}
      </main>

      {order && selectedAsset && <div className="inv-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOrder(null); }}>
        <section className="inv-order-modal" role="dialog" aria-modal="true" aria-labelledby="inv-order-title">
          <button type="button" className="inv-modal-close" aria-label="Close order dialog" onClick={() => setOrder(null)}><FiX size={19} /></button>
          <span className="inv-eyebrow">WALLET ORDER / {order.side.toUpperCase()}</span>
          <h2 id="inv-order-title">{order.side === "buy" ? "Buy" : "Sell"} {selectedAsset.symbol}</h2>
          <p className="inv-modal-subtitle">{selectedAsset.name} Â· market quote {money.format(selectedAsset.price)} per share</p>
          <form onSubmit={submitOrder}>
            {order.side === "buy" ? (
              <>
                <label className="inv-quantity-label" htmlFor="inv-buy-amount">Amount to invest ({portfolio.currencyCode})</label>
                <input id="inv-buy-amount" type="number" inputMode="decimal" min="0.01" max={portfolio.cash} step="0.01" value={buyAmount} onChange={(event) => { setBuyAmount(event.target.value); setOrderError(""); }} autoFocus />
                <div className="inv-order-summary"><span>Investment amount</span><strong>{formatWalletMoney(Number(buyAmount) || 0, portfolio.currencyCode)}</strong></div>
                <div className="inv-order-summary"><span>Available wallet balance</span><strong>{formatWalletMoney(portfolio.cash, portfolio.currencyCode)}</strong></div>
              </>
            ) : (
              <>
                <label className="inv-quantity-label" htmlFor="inv-quantity">Share quantity</label>
                <input id="inv-quantity" type="number" inputMode="decimal" min="0.00000001" step="0.00000001" value={quantity} onChange={(event) => { setQuantity(event.target.value); setOrderError(""); }} autoFocus />
                <div className="inv-order-summary"><span>Estimated order value (USD)</span><strong>{money.format(Number.isFinite(orderValue) ? orderValue : 0)}</strong></div>
                <div className="inv-order-summary"><span>Shares available</span><strong>{sharesFormat.format(selectedHolding ? selectedHolding.shares : 0)}</strong></div>
              </>
            )}
            {orderError && <p className="inv-order-error" role="alert">{orderError}</p>}
            <div className="inv-modal-warning">The API checks its current quote and exchange rate when submitted. Buys debit your available wallet balance; sells credit it.</div>
            <button type="submit" className={order.side === "buy" ? "inv-confirm-buy" : "inv-confirm-sell"} disabled={orderSubmitting || portfolioLoading || Boolean(portfolioError)}>{orderSubmitting ? "Savingâ¦" : `Confirm wallet ${order.side}`}</button>
          </form>
        </section>
      </div>}
    </section>
  );
}
